import { Injectable } from "@angular/core";
import * as CryptoJS from "crypto-js";
import { BehaviorSubject, Observable, firstValueFrom } from "rxjs";
import { Router, UrlTree } from "@angular/router";
import { HttpResult } from "../../core/services/base-http.service";
import { BaseResponse } from "../../shared/models/base-response.model";
import {
  CodeExchangeResponseModel,
  TokenModel,
  TokenPayload,
  VerifyLinkLoginResponseModel,
  LinkLoginAdditionalData,
  defaultRouteAfterLogin,
} from "./auth.model";
import { AuthApiService } from "./auth-api.service";
import { StorageService } from "../../core/services/storage.service";

@Injectable({ providedIn: "root" })
export class AuthBllService {
  /**
   * Emits the current authentication state: `true` if a valid (unexpired)
   * ID token is present, otherwise `false`.
   */
  private readonly isAuthenticatedSubject = new BehaviorSubject<boolean>(false);

  /**
   * @param api      Service for making auth-related HTTP calls (exchange code, build URLs).
   * @param storage  Client-side storage for tokens, payloads, and PKCE verifiers.
   * @param router   Angular Router, used to redirect on logout.
   */
  constructor(
    private api: AuthApiService,
    private storage: StorageService,
    private router: Router
  ) {
    this._updateAuthState();
  }

  /**
   * Persists all token values into storage and extracts
   * payload data for later use.
   *
   * @param tokens  Object containing access, ID, and refresh tokens.
   */
  saveTokens(tokens: TokenModel): void {
    const now = Math.floor(Date.now() / 1000);
    const expiresAt = now + tokens.expires_in;

    this.storage.saveAccessToken(tokens.access_token);
    if (tokens.id_token) {
      this.storage.saveIdToken(tokens.id_token);
      const payload = this.decodeJwt(tokens.id_token);
      if (payload) {
        this.storage.saveDecodedTokenPayload(JSON.stringify(payload));
        const user_id = payload.sub;
        if (user_id) {
          this.storage.saveLoggedUserId(user_id.toString());
        }
      }
    }
    if (tokens.refresh_token) {
      this.storage.saveRefreshToken(tokens.refresh_token);
    }
    this.storage.saveExpiresAt(expiresAt.toString());
  }

  /**
   * Decodes the payload of a JWT without validating its signature.
   *
   * @param token  The full JWT string.
   * @returns      Parsed payload conforming to TokenPayload.
   * @throws       Error if the JWT is malformed.
   */
  private decodeJwt(token: string): TokenPayload {
    const parts = token.split(".");
    if (parts.length !== 3) {
      throw new Error("Invalid JWT format");
    }
    const payloadBase64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(payloadBase64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  }

  /**
   * Exposes an observable stream of the current auth status.
   * Subscribe to respond to login/logout events.
   */
  get isAuthenticated$(): Observable<boolean> {
    return this.isAuthenticatedSubject.asObservable();
  }

  /**
   * Initiates the PKCE/OAuth2 login flow:
   * - Generates a random code verifier.
   * - Computes its SHA256 challenge.
   * - Stores the verifier for later.
   * - Redirects browser to the authorization URL.
   */
  startAuthenticate(): void {
    const verifier = this._generateCodeVerifier(128);
    const challenge = this._computeCodeChallenge(verifier);
    this.storage.saveCodeVerifier(verifier);
    const state = this.storage.getState() ?? defaultRouteAfterLogin;
    const refSource = this.storage.getRefSource() || '';


    // redirect to OAuth server for user consent
    window.location.replace(this.api.buildAuthorizationUrl(challenge, state,refSource));
  }

  /**
   * Completes the PKCE flow by exchanging the auth code for tokens.
   * Persists tokens on success, or throws on error.
   *
   * @param authKey  An application-specific key or client identifier.
   * @param code     Authorization code returned by the OAuth server.
   */

  async completeAuthenticate(authKey: string, code: string, refSource?: string): Promise<void> {
    const verifier = this.storage.getCodeVerifier();
    if (!verifier) {
      throw new Error("Missing PKCE code verifier – cannot complete login.");
    }

    // Fire the request and await the unified { status, body } result
    const result: HttpResult<BaseResponse<CodeExchangeResponseModel>> =
      await firstValueFrom(
        this.api.exchangeCodeForToken(authKey, code, verifier,refSource)
      );

    const { status, body } = result;

    // 2xx OK
    if (status === 200) {
      // unwrap your tokens (adjust path if needed)
      const tokens: TokenModel = body.data.tokens;
      this.saveTokens(tokens);
      this._updateAuthState();
      return;
    } else {
      throw new Error(body.message || "Code exchange failed");
    }
  }

  /**
   * Logs the user out by clearing all auth data and
   * navigating back to the login/init page.
   */
  logout(): void {
    this.storage.clearAll();
    this.storage.clearRefSource();
    this.storage.clearPavilianData();
    this.isAuthenticatedSubject.next(false);
    console.log("User logged out");

    if (this.router.url.includes("/home")) {
      window.location.reload();
    }else{
      this.router.navigate(["/home"]);
    }
  }

  /**
   * Re-evaluates stored ID token + payload to determine
   * if the user is still authenticated, and emits the result.
   */
  private _updateAuthState(): void {
    const idToken = this.storage.getIdToken();
    const payload =
      this.storage.getDecodedTokenPayload() as TokenPayload | null;
    const valid = !!idToken && !!payload && payload.exp * 1000 > Date.now();
    this.isAuthenticatedSubject.next(valid);
  }
  /**
   * Generates a random string for use as a PKCE code verifier.
   *
   * @param length  Desired length of the verifier string.
   * @returns       Random alphanumeric string.
   */

  private _generateCodeVerifier(length: number): string {
    let result = "";
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }
  /**
   * Computes the SHA-256-based PKCE code challenge.
   *
   * @param verifier  The code verifier to hash.
   * @returns         Hex-encoded SHA256 hash of `verifier`.
   */

  private _computeCodeChallenge(verifier: string): string {
    const codeVerifierHash = CryptoJS.SHA256(verifier);
    const codeChallenge = codeVerifierHash.toString();
    return codeChallenge;
  }

  /**
   * Completes the PKCE flow by exchanging the auth code for tokens.
   * Persists tokens on success, or throws on error.
   *
   * @param authKey  An application-specific key or client identifier.
   * @param code     Authorization code returned by the OAuth server.
   */

  async processLinkClick(authKey: string, isGallabox: boolean): Promise<void> {
    const logged_id = this.storage.getLoggedUserId();

    // Fire the request and await the unified { status, body } result
    const result: HttpResult<BaseResponse<VerifyLinkLoginResponseModel>> =
      await firstValueFrom(this.api.verifyLinkLogin(authKey, logged_id, isGallabox));

    const { status, body } = result;

    // 2xx OK
    if (status === 200) {     
      // unwrap your tokens (adjust path if needed)     
      if (body.data.tokens != null || body.data.tokens != undefined) {
        const tokens: TokenModel = body.data.tokens!;
        if (tokens.id_token) {
          this.saveTokens(tokens);
          this._updateAuthState();
        }
      }
      this.processLinkLoginRedirect(body.data.additional_data!);
    } else {
      // throw new Error(body.message || "verify link login failed");
      this.router.navigate(["/home"]);
    }
  }
  /**
   * Processes the link login redirect based on the provided additional data.
   *
   * This method checks if the additional data contains a valid redirect URL and required user information
   * (first name, last name, state, and city). If any of the required information is missing, it saves the
   * redirect URL to storage and navigates the user to the home page. If all required information is present,
   * it navigates the user to the appropriate route based on the redirect value.
   *
   * @param additionalData - The additional data containing redirect URL and user information.
   * @param additionalData.redirect - The URL to redirect to after processing the login.
   * @param additionalData.first_name - The first name of the user.
   * @param additionalData.last_name - The last name of the user.
   * @param additionalData.state - The state of the user.
   * @param additionalData.city - The city of the user.
   *
   * @returns void
   */
  processLinkLoginRedirect(additionalData: LinkLoginAdditionalData): void {
    if (!additionalData || !additionalData.redirect) {
      console.warn(
        "No additional data or redirect URL provided for link login."
      );
      this.router.navigate(["home"]);
    } else {
      if (additionalData.eventcode) {
        this.storage.saveEventCode(additionalData.eventcode);
      }
      // save ref source 
      if(additionalData.ref_source){
        this.storage.saveRefSource(additionalData.ref_source);
      }
      //If the additional data does not contains or striped empty first_name , last_bame , state and city check it
      if (
        !additionalData.first_name ||
        !additionalData.last_name ||
        !additionalData.state ||
        !additionalData.city ||
        !this.validateName(additionalData.first_name) ||
        !this.validateName(additionalData.last_name)
      ) {
        console.warn("Incomplete additional data for link login.");
        //add redirect to stirage and return to edit profile
        this.storage.saveLinkLoginRedirect(additionalData.redirect);
        //save query params to storage if any
        switch (additionalData.redirect) {
          case "invite_details":
            // Handle accept invite logic here
            if (additionalData.invite_code) {
              const queryParams = {
                code: additionalData.invite_code,
              };
              this.storage.saveLinkLoginRedirectQueryParams(queryParams);
            }
            break;
          default:
            break;
        }
        //navigate to edit profile once ready till then redirect to home
        this.router.navigate(["reg/edit-basic-profile"]);
      } else {
        // switch case for redirect value
        switch (additionalData.redirect) {
          case "ticket_details":
            if (additionalData.ticket_code) {
              this.router.navigate(["reg/ticket-details"], {
                queryParams: {
                  code: additionalData.ticket_code,
                },
              });
            } else {
              this.router.navigate(["home"]);
            }
            break;
          case "assign_tickets":
            this.router.navigate(["reg/assign-ticket"]);
            break;
          case "buy_tickets":
              this.router.navigate(["reg/select-ticket"]);
              break;
          case "catalog_pavilions":
              this.router.navigate(["catalogue/pavilion-list"]);
              break;
          case "my_tickets":
            this.router.navigate(["reg/my-tickets"]);
            break;
          case "summary_details":
            if (additionalData.summary_id) {
              this.storage.saveSummaryId(additionalData.summary_id);
              this.router.navigate(["reg/ticket-summary"]);
            }else {
              this.router.navigate(["home"]);
            }
            break;
          case "invite_details":
            // Handle accept invite logic here
            if (additionalData.invite_code) {
              this.router.navigate(["exh-invite/details"], {
                queryParams: {
                  code: additionalData.invite_code,
                },
              });
            } else {
              this.router.navigate(["home"]);
            }
            break;
          case "exh_invite_pending_list":
              this.router.navigate(["exh-invite/list"]);
              break;
          default:
            console.warn("Unknown redirect value:", additionalData.redirect);
            this.router.navigate(["home"]);
        }
      }
    }
  }

  getUserInfo(): { name: any; image: any } {
    return {
      name: this.storage.getUserName(),
      image: this.storage.getUserImgUrl(),
    };
  }

  /**
   * Saves a value to storage based on the specified key.
   *
   * Depending on the provided key, this method delegates the value to the appropriate
   * storage-saving method:
   * - `"state"`: Saves the value as the application state.
   * - `"justAuthenticated"`: Saves the value indicating recent authentication.
   * - `"virtualEventPending"`: Saves the value indicating a pending virtual event.
   * - `"hasCategoryFilled"`: Saves the value indicating whether the user has filled their category.
   * - `"accessToVirtualEvent"`: Saves the value indicating access to a virtual event.
   * If the key does not match any of the above, the method does nothing.
   *
   * @param key - The storage key determining which value to save.
   * @param value - The value to be saved in storage.
   */
  saveStorageValue(key: string, value: string): void {
    switch (key) {
      case "state":
        this.storage.saveState(value);
        break;
      case "justAuthenticated":
        this.storage.saveJustAuthenticated(value);
        break;
      case "virtualEventPending":
        this.storage.saveVirtualEventPending(value);
        break;
      case "hasCategoryFilled":
        this.storage.saveHasCategoryFilled(value);
        break;
      case "accessToVirtualEvent":
        this.storage.saveHasAccessToVirtualEvent(value);
        break;
      default:
        return;
    }
  }

  /**
   * Retrieves a value from storage based on the provided key.
   *
   * @param key - The key identifying the value to retrieve. Supported keys are:
   * - `"state"`: Returns the current state from storage.
   * - `"justAuthenticated"`: Returns the just authenticated status.
   * - `"virtualEventPending"`: Returns the virtual event pending status.
   * - `"hasCategoryFilled"`: Returns the category filled status.
   * - `"accessToVirtualEvent"`: Returns the access status to a virtual event.
   * @returns The value associated with the given key, or `null` if the key is not recognized.
   */
  getStorageValue(key: string): string | null {
    switch (key) {
      case "state":
        return this.storage.getState();
      case "justAuthenticated":
        return this.storage.getJustAuthenticated();
      case "virtualEventPending":
        return this.storage.getVirtualEventPending();
      case "hasCategoryFilled":
        return this.storage.getHasCategoryFilled();
      case "accessToVirtualEvent":
        return this.storage.getAccessToVirtualEvent();
      default:
        return null;
    }
  }

  /**
   * Removes a specific item from storage based on the provided key.
   *
   * @param key - The key identifying which storage item to remove. Supported keys are:
   *   - "state": Clears the stored state.
   *   - "justAuthenticated": Clears the just authenticated flag.
   *   - "virtualEventPending": Clears the virtual event pending flag.
   *   If the key does not match any of these, no action is taken.
   */
  removeStorageItem(key: string): void {
    switch (key) {
      case "state":
        this.storage.clearState();
        break;
      case "justAuthenticated":
        this.storage.clearJustAuthenticated();
        break;
      case "virtualEventPending":
        this.storage.clearVirtualEventPending();
        break;
      default:
        break;
    }
  }

  validateName(name: string): boolean {
    // Check for missing or empty name
    if (!name) return false;

    // leading or trailing spaces
    if (/^\s+|\s+$/.test(name)) return false;

    // length constraint
    if (name.length < 1 || name.length > 60) return false;

    // contains digits
    if (/\d/.test(name)) return false;

    // must start with a letter (using Unicode property escape)
    if (!/^\p{L}/u.test(name)) return false;

    // consecutive special chars (., -, ', ’) or spaces
    if (/(?:[.\-'\’]){2,}| {2,}/.test(name)) return false;

    // allowed characters only: letters, marks, zero-width joiner, space, . ' - ’
    if (!/^[\p{L}\p{M}\u200D .'\-’]+$/u.test(name)) return false;

    // if all checks passed
    return true;
  }

  stripQueryParams(urlTree: UrlTree): string {
    const clonedTree = this.router.parseUrl(
      this.router.serializeUrl(urlTree)
    );
    clonedTree.queryParams = {}; // remove query params
    return this.router.serializeUrl(clonedTree);
  }

}
